import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { money, priceLabel, locationLabel, schemeBadge, dealSavings, daysUntil, fmtDate } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, Users, ArrowRight, Tag, ShieldCheck, LogIn, MapPin, CalendarClock, Navigation, Loader2 } from "lucide-react";
import SubmitDealDialog from "@/components/SubmitDealDialog";
import { useAuth } from "@/context/AuthContext";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "curated", label: "Curated" },
  { key: "community", label: "Community" },
];

const PROVINCES = [
  ["", "All provinces"], ["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"],
  ["NB", "New Brunswick"], ["NL", "Newfoundland and Labrador"], ["NS", "Nova Scotia"],
  ["NT", "Northwest Territories"], ["NU", "Nunavut"], ["ON", "Ontario"], ["PE", "Prince Edward Island"],
  ["QC", "Quebec"], ["SK", "Saskatchewan"], ["YT", "Yukon"],
];

export default function Browse() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const prefs = (() => {
    try { return JSON.parse(localStorage.getItem("bogoshare_browse_prefs")) || {}; } catch { return {}; }
  })();
  const [deals, setDeals] = useState(null);
  const [cities, setCities] = useState([]);
  const [filter, setFilter] = useState(prefs.filter || "all");
  const [scheme, setScheme] = useState(prefs.scheme || "all");
  const [province, setProvince] = useState(prefs.province || "");
  const [city, setCity] = useState(prefs.city || "");
  const [sort, setSort] = useState("newest");
  const [radius, setRadius] = useState(prefs.radius || "any");
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    localStorage.setItem("bogoshare_browse_prefs", JSON.stringify({ filter, scheme, province, city, radius }));
  }, [filter, scheme, province, city, radius]);

  useEffect(() => {
    api.get("/deals/cities").then(({ data }) => setCities(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (f = filter, prov = province, s = sort, c = coords, r = radius, sc = scheme, ct = city) => {
    setDeals(null);
    const params = {};
    if (f !== "all") params.source = f;
    if (sc !== "all") params.deal_scheme = sc;
    if (ct) params.city = ct;
    else if (prov) params.province = prov;
    if (s === "nearest" && c) {
      params.sort = "nearest";
      params.lat = c.lat;
      params.lng = c.lng;
      if (r !== "any") params.radius_km = r;
    }
    const { data } = await api.get("/deals", { params });
    setDeals(data);
  };

  useEffect(() => {
    load(filter, province, sort, coords, radius, scheme, city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, scheme, province, city, sort, coords, radius]);

  const enableNearest = () => {
    if (coords) {
      setSort("nearest");
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation isn't supported by your browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSort("nearest");
        setLocating(false);
        toast.success("Showing deals nearest to you.");
      },
      () => {
        setLocating(false);
        toast.error("Location access denied. Enable it to sort by distance.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  };

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-4xl tracking-tight" style={{ fontWeight: 900 }}>Deal board</h1>
            <p className="mt-2 text-muted-foreground">Curated Buy-One-Get-One offers. Claim a slot to find your split partner.</p>
          </div>
          {user ? (
            <SubmitDealDialog onSubmitted={() => load(filter, province, sort, coords)} />
          ) : (
            <Button data-testid="login-to-submit-btn" onClick={() => navigate("/login")} className="rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200">
              <LogIn className="mr-1 h-4 w-4" /> Log in to submit
            </Button>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white p-1" data-testid="deal-filter">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  filter === f.key ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white p-1" data-testid="scheme-filter">
            {[["all", "All types"], ["free", "Free"], ["discount", "Discounted"], ["multipack", "Multi-pack"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`scheme-${key}`}
                onClick={() => setScheme(key)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  scheme === key ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <Select value={province || "all"} onValueChange={(v) => { setProvince(v === "all" ? "" : v); if (v !== "all") setCity(""); }}>
            <SelectTrigger data-testid="province-filter" className="w-[190px] rounded-full bg-white">
              <MapPin className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
              <SelectValue placeholder="All provinces" />
            </SelectTrigger>
            <SelectContent>
              {PROVINCES.map(([code, label]) => (
                <SelectItem key={code || "all"} value={code || "all"} data-testid={`province-opt-${code || "all"}`}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={city || "all"} onValueChange={(v) => { setCity(v === "all" ? "" : v); if (v !== "all") setProvince(""); }}>
            <SelectTrigger data-testid="city-filter" className="w-[190px] rounded-full bg-white">
              <MapPin className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
              <SelectValue placeholder="All cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="city-opt-all">All cities</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c.city} value={c.city} data-testid={`city-opt-${c.city}`}>
                  {c.city}{c.province ? `, ${c.province}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white p-1" data-testid="sort-toggle">
            <button
              data-testid="sort-newest"
              onClick={() => setSort("newest")}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                sort === "newest" ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"
              }`}
            >
              Newest
            </button>
            <button
              data-testid="sort-nearest"
              onClick={enableNearest}
              disabled={locating}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                sort === "nearest" ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"
              }`}
            >
              {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Navigation className="h-3.5 w-3.5" />}
              Nearest first
            </button>
            <button
              data-testid="sort-savings"
              onClick={() => setSort("savings")}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                sort === "savings" ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"
              }`}
            >
              Biggest savings
            </button>
          </div>

          {sort === "nearest" && coords && (
            <Select value={String(radius)} onValueChange={(v) => setRadius(v === "any" ? "any" : Number(v))}>
              <SelectTrigger data-testid="radius-filter" className="w-[160px] rounded-full bg-white">
                <Navigation className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Any distance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any" data-testid="radius-opt-any">Any distance</SelectItem>
                <SelectItem value="10" data-testid="radius-opt-10">Within 10 km</SelectItem>
                <SelectItem value="25" data-testid="radius-opt-25">Within 25 km</SelectItem>
                <SelectItem value="50" data-testid="radius-opt-50">Within 50 km</SelectItem>
                <SelectItem value="100" data-testid="radius-opt-100">Within 100 km</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {deals === null ? (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={`deal-skel-${i}`} className="h-80 rounded-xl" />
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="mt-20 text-center text-muted-foreground">No active deals right now. Check back soon.</div>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(sort === "savings" ? [...deals].sort((a, b) => dealSavings(b) - dealSavings(a)) : deals).map((d, i) => (
              <motion.div
                key={d.deal_id}
                data-testid={`deal-card-${d.deal_id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="group flex flex-col overflow-hidden rounded-xl border border-black/10 bg-white hover:-translate-y-1 transition-transform duration-200"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={d.image_url} alt={d.product_name} className="h-full w-full object-cover" />
                  {(() => { const sb = schemeBadge(d); return (
                    <Badge data-testid={`deal-scheme-badge-${d.deal_id}`} className={`absolute top-3 left-3 border-none ${sb.cls}`}>{sb.text}</Badge>
                  ); })()}
                  {d.my_match && (
                    <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground border-none">Joined</Badge>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                      <Store className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{d.retailer}</span>
                      <span className="mx-1">·</span>
                      <Tag className="h-3.5 w-3.5 shrink-0" /> {d.category}
                    </div>
                    {d.deal_source === "community" ? (
                      <span data-testid="badge-community" className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent px-2 py-0.5 text-[10px] font-semibold shrink-0">
                        <Users className="h-3 w-3" /> Community
                      </span>
                    ) : (
                      <span data-testid="badge-curated" className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold shrink-0">
                        <ShieldCheck className="h-3 w-3" /> Curated
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 font-heading text-lg leading-snug" style={{ fontWeight: 600 }}>{d.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{d.description}</p>

                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {locationLabel(d)}</span>
                    {typeof d.distance_km === "number" && (
                      <span data-testid={`distance-${d.deal_id}`} className="flex items-center gap-1 text-primary font-medium">
                        <Navigation className="h-3.5 w-3.5" /> ~{d.distance_km} km away
                      </span>
                    )}
                    {d.expires_at && (() => {
                      const dleft = daysUntil(d.expires_at);
                      if (dleft !== null && dleft >= 0 && dleft <= 5) {
                        return (
                          <span data-testid={`ends-soon-${d.deal_id}`} className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 font-semibold">
                            <CalendarClock className="h-3.5 w-3.5" />
                            {dleft === 0 ? "Ends today" : dleft === 1 ? "Ends tomorrow" : `Ends in ${dleft} days`}
                          </span>
                        );
                      }
                      return <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Ends {fmtDate(d.expires_at)}</span>;
                    })()}
                  </div>

                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Full price</p>
                      <p className="font-heading text-xl" style={{ fontWeight: 700 }}>{priceLabel(d)}</p>
                    </div>
                    {d.price_type !== "tbc" && (
                      <div className="text-right">
                        <p className="text-xs text-accent font-semibold">You save up to</p>
                        <p data-testid={`deal-savings-${d.deal_id}`} className="font-heading text-xl text-accent" style={{ fontWeight: 700 }}>{money(dealSavings(d))}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-black/5 pt-4">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Users className="h-3.5 w-3.5" /> {d.open_slots} looking for a partner
                    </span>
                    <Button
                      size="sm"
                      data-testid={`view-deal-${d.deal_id}`}
                      onClick={() => navigate(`/deals/${d.deal_id}`)}
                      className="rounded-full"
                    >
                      View <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
